import { AlertTriangle, Edit01, MarkerPin01, Modem02 } from "@untitledui/icons";
import { useEffect, useMemo, useRef } from "react";

import { Badge } from "@/components/ui/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";

import type { ShadowSensor } from "../../../types/location-map";

import {
	type RowActionItem,
	RowActionsMenu,
} from "../../../components/row-actions-menu";
import { SENSOR_MISSING_COORDINATES_TOOLTIP } from "../../../utils/location/location-map-utils";
import { sensorStatusConfig } from "../../../utils/sensor/sensor-status";
import { SENSOR_TYPE_ICON_MAP } from "../../../utils/sensor/sensor-type";
import { isValidCoordinatePair } from "../../../utils/shared/coordinates";
import { toTitleCase } from "../../../utils/shared/strings";

interface SensorRowProps {
	allowScrollIntoView?: boolean;
	canEditSensorDetails: boolean;
	editingLabel?: string;
	isActive: boolean;
	isEditDisabled: boolean;
	isHovered?: boolean;
	onAddSensor?: () => void;
	onEdit: () => void;
	onFocus: () => void;
	onHoverEnd?: () => void;
	onHoverStart?: () => void;
	sensor: ShadowSensor;
}

export const SensorRow = ({
	allowScrollIntoView = true,
	canEditSensorDetails,
	editingLabel,
	isActive,
	isEditDisabled,
	isHovered = false,
	onAddSensor,
	onEdit,
	onFocus,
	onHoverEnd,
	onHoverStart,
	sensor,
}: SensorRowProps) => {
	const status = sensorStatusConfig(sensor.status);
	const rowRef = useRef<HTMLDivElement>(null);
	const hasCoordinates = isValidCoordinatePair(
		sensor.geolocation?.coordinates ?? undefined,
	);
	const SensorTypeIcon = useMemo(() => {
		return SENSOR_TYPE_ICON_MAP[sensor.type.toLowerCase()] ?? MarkerPin01;
	}, [sensor.type]);

	const menuItems = useMemo((): RowActionItem[] => {
		if (!canEditSensorDetails || editingLabel) return [];
		return [
			{
				icon: Edit01,
				id: "edit-sensor",
				isDisabled: isEditDisabled,
				label: "Edit sensor",
				onAction: onEdit,
			},
			onAddSensor
				? {
						icon: Modem02,
						id: "add-sensor",
						isDisabled: isEditDisabled,
						label: "Add sensor",
						onAction: onAddSensor,
					}
				: null,
		].filter(Boolean) as RowActionItem[];
	}, [canEditSensorDetails, editingLabel, isEditDisabled, onAddSensor, onEdit]);

	useEffect(() => {
		// Avoid scrollIntoView while the sidebar is off-screen (translate-x-full):
		// the browser will scroll ancestors to reveal the row, which makes the
		// closed drawer look open while it stays pointer-events-none / inert.
		if (!isActive || !allowScrollIntoView) return;

		rowRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "nearest",
		});
	}, [allowScrollIntoView, isActive]);

	return (
		<div
			className={cx(
				"flex items-center justify-between gap-3 border-l-2 py-3 pr-3 pl-4 transition-colors",
				isActive
					? "border-brand bg-brand-primary_alt"
					: isHovered
						? "bg-secondary_subtle border-transparent"
						: "hover:bg-secondary_subtle border-transparent",
			)}
			onMouseEnter={onHoverStart}
			onMouseLeave={onHoverEnd}
			ref={rowRef}
		>
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<button
					aria-current={isActive ? "true" : undefined}
					className="min-w-0 flex-1 text-left"
					data-active={isActive ? "true" : "false"}
					disabled={isEditDisabled}
					onClick={onFocus}
					type="button"
				>
					<p className="text-[11px] leading-none font-medium tracking-wide text-tertiary uppercase">
						Sensor
					</p>
					<p
						className={cx(
							"mt-0.5 truncate text-sm font-medium",
							isActive ? "text-brand-secondary" : "text-primary",
						)}
					>
						{toTitleCase(sensor.name)}
					</p>
					<p className="mt-0.5 flex items-center gap-1 truncate text-xs text-tertiary">
						<SensorTypeIcon
							aria-hidden
							className="size-3.5 shrink-0 text-fg-quaternary"
						/>
						<span className="truncate">{toTitleCase(sensor.type)}</span>
					</p>
				</button>
				{!hasCoordinates ? (
					<Tooltip placement="top" title={SENSOR_MISSING_COORDINATES_TOOLTIP}>
						<TooltipTrigger
							aria-label={SENSOR_MISSING_COORDINATES_TOOLTIP}
							className="shrink-0"
						>
							<AlertTriangle className="size-4 shrink-0 text-utility-warning-500" />
						</TooltipTrigger>
					</Tooltip>
				) : null}
			</div>
			<div className="flex shrink-0 items-center gap-2">
				{editingLabel ? (
					<span className="text-brand text-xs font-medium">{editingLabel}</span>
				) : (
					<Badge color={status.color} size="sm" type="pill-color">
						{status.label}
					</Badge>
				)}
				{menuItems.length > 0 ? (
					<RowActionsMenu
						ariaLabel={`Actions for ${toTitleCase(sensor.name)}`}
						isDisabled={isEditDisabled}
						items={menuItems}
					/>
				) : null}
			</div>
		</div>
	);
};
